/**
 * ONE TAP — the single source of truth for every tunable number in the game.
 *
 * Units: meters, seconds, radians. +Y is up. Nothing outside this file should
 * contain a magic gameplay number; if you need one, add it here with a comment
 * explaining what it does to the feel.
 */

// ---------------------------------------------------------------------------
// Simulation timing
// ---------------------------------------------------------------------------

/** Authoritative server tick rate. Client prediction runs the same fixed step. */
export const TICK_HZ = 30;
export const TICK_DT = 1 / TICK_HZ;
export const TICK_MS = 1000 / TICK_HZ;

/** Client renders at display refresh; the sim always steps at TICK_DT. Never step the sim with a raw frame dt. */
export const MAX_TICKS_PER_FRAME = 5; // spiral-of-death guard after a tab stall

// ---------------------------------------------------------------------------
// Player body
// ---------------------------------------------------------------------------

/**
 * Playtest: "the characters just kind of feel too small — short and tiny."
 *
 * Scaled the whole body up ~14% from a realistic 1.8m to a heroic 2.05m. Game characters
 * almost always read smaller than their nominal height because the arena is oversized and the
 * FOV is wide (92-112 deg), which shrinks everything not right in front of the camera. Heroic
 * proportions are the norm in this genre for exactly that reason.
 *
 * Everything below scales together — eye height, crouch, and the head hitbox — because the
 * headshot is an instant kill and its hitbox must stay honestly attached to the visible head.
 */
export const PLAYER_HEIGHT = 2.05;
export const PLAYER_CROUCH_HEIGHT = 1.15;
export const PLAYER_RADIUS = 0.44;
/** Eye offset from the capsule base. */
export const PLAYER_EYE_HEIGHT = 1.85;
export const PLAYER_CROUCH_EYE_HEIGHT = 0.98;

/** Head hitbox: a sphere centered this far above the capsule base. Headshots are instant kills, so this must be honest. */
export const HEAD_HEIGHT = 1.82;
export const HEAD_RADIUS = 0.25;
export const HEAD_CROUCH_HEIGHT = 0.98;

/** Max slope (radians from horizontal) the player can stand on. Steeper counts as a wall. */
export const MAX_GROUND_SLOPE = 46 * (Math.PI / 180);
/** Steps up to this height are climbed silently instead of blocking. */
export const STEP_HEIGHT = 0.45;

// ---------------------------------------------------------------------------
// Ground movement
// ---------------------------------------------------------------------------

export const GRAVITY = -26.0; // punchier than real gravity; arcs stay readable
/**
 * Playtest: "if anything, the movement should be faster... the actual physical plane movement
 * should be faster, but the animation should be half as fast or even less."
 *
 * That pairing is the whole point: fast translation with a SLOW, long-striding gait reads as
 * weight and momentum. Fast translation with a fast gait reads as "a really high-gear bicycle"
 * — legs spinning, going nowhere. Speed up here; slow the cadence in playerRig's STRIDE_LENGTH.
 */
export const RUN_SPEED = 11.5;
export const GROUND_ACCEL = 95.0;
export const GROUND_FRICTION = 7.5;
/** Below this speed friction snaps you to a stop instead of asymptoting. */
export const STOP_SPEED = 2.5;

/** Air control. AIR_ACCEL is deliberately low while AIR_STRAFE_ACCEL is high — that pairing is what makes
 *  Quake-style air-strafing work: you can't add speed by holding forward, only by turning into your strafe. */
export const AIR_ACCEL = 12.0;
export const AIR_STRAFE_ACCEL = 90.0;
export const AIR_SPEED_CAP = 1.6; // wishspeed cap for the strafe term
export const AIR_FRICTION = 0.0; // momentum is sacred

export const JUMP_VELOCITY = 9.2;
export const DOUBLE_JUMP_VELOCITY = 8.4;
/** Jump still fires this long after walking off a ledge. */
export const COYOTE_TIME_MS = 120;
/** A jump pressed this long before landing fires on touchdown. */
export const JUMP_BUFFER_MS = 140;

// ---------------------------------------------------------------------------
// Slide + surf
// ---------------------------------------------------------------------------

/** Minimum horizontal speed required to enter a slide. */
export const SLIDE_MIN_SPEED = 6.0;
/** Instant speed kick on slide entry — makes initiating feel good. */
export const SLIDE_BOOST = 3.2;
export const SLIDE_FRICTION = 1.4; // much lower than standing friction
export const SLIDE_MAX_SPEED = 24.0;
/** Downhill acceleration multiplier — this is the "surf" in momentum surfing. */
export const SLIDE_SLOPE_ACCEL = 30.0;
export const SLIDE_MIN_MS = 220; // commitment window; prevents slide-spam
export const SLIDE_STEER_RATE = 2.6; // rad/s of yaw authority while sliding

/** Landing on a downslope within this window of a slide input refunds momentum instead of eating it. */
export const SURF_LAND_GRACE_MS = 160;
export const SURF_LAND_REFUND = 0.85; // fraction of pre-landing speed preserved

// ---------------------------------------------------------------------------
// Dash
// ---------------------------------------------------------------------------

export const DASH_CHARGES = 1;
export const DASH_CHARGES_UNARMED = 2; // being gunless makes you the mobile threat
export const DASH_SPEED = 17.0;
export const DASH_DURATION_MS = 130;
export const DASH_RECHARGE_MS = 1400;
/** Dash refills instantly on ground contact (down to this many charges) so ground play stays fluid. */
export const DASH_GROUND_REFILL = true;
export const DASH_VERTICAL_DAMP = 0.25; // dashes are mostly horizontal
/** Sliding within this window after a dash lands converts dash speed into slide speed. */
export const DASH_TO_SLIDE_WINDOW_MS = 250;
export const DASH_TO_SLIDE_BONUS = 1.25;

// ---------------------------------------------------------------------------
// Wall-run + wall-jump
// ---------------------------------------------------------------------------

export const WALLRUN_MIN_SPEED = 7.0;
/** Wall must be within this many radians of vertical to be runnable. */
export const WALLRUN_MAX_WALL_TILT = 20 * (Math.PI / 180);
/** How much gravity applies while wall-running (0 = none, 1 = full). Ramps toward 1 as the run decays. */
export const WALLRUN_GRAVITY_SCALE = 0.15;
export const WALLRUN_MAX_MS = 1800;
export const WALLRUN_ACCEL = 24.0;
export const WALLRUN_MAX_SPEED = 16.0;
/** Sticks you to the wall so small geometry bumps don't eject you. */
export const WALLRUN_STICK_FORCE = 6.0;
export const WALLRUN_MIN_HEIGHT = 1.2; // can't wall-run at floor level
/**
 * Camera roll while wall-running. Raised from 14 deg: the movement pass measured wall-run
 * attach going from 89% to 99%, which means players now wall-run constantly — and the roll is
 * the only signal telling them it is happening. 14 deg was too subtle for a state change that
 * has to read instantly at speed.
 */
export const WALLRUN_CAMERA_ROLL = 21 * (Math.PI / 180);

export const WALLJUMP_UP_VELOCITY = 8.6;
export const WALLJUMP_PUSH_VELOCITY = 9.0; // away from the wall
export const WALLJUMP_FORWARD_KEEP = 0.85; // fraction of along-wall speed preserved
/** You cannot re-attach to the same surface until you touch ground or a different wall.
 *  Without this you can ladder infinitely up a single flat wall. */
export const WALLRUN_SAME_SURFACE_LOCKOUT = true;
export const WALLRUN_REATTACH_COOLDOWN_MS = 260;

// ---------------------------------------------------------------------------
// Grapple
// ---------------------------------------------------------------------------

/**
 * `PlayerState.grappleAnchorId` sentinels.
 *
 * The grapple attaches to ANY surface you are aiming at, not only to placed anchors —
 * playtest: "I thought the grapple was just going to be, you can grapple any wall wherever
 * you're aiming as long as it's within range." That expectation is better than the original
 * design: it makes traversal fluid and continuous instead of a hunt for fixed points, and it
 * dissolves the discoverability problem that made the mechanic feel broken.
 *
 * Placed anchors survive as generous aim-assist targets. They hang in open air over the void
 * where a raycast would hit nothing at all, so they still buy swings that free-aim cannot.
 */
export const GRAPPLE_NONE = -1;
export const GRAPPLE_SURFACE = -2;

export const GRAPPLE_RANGE = 60.0;
/**
 * Aim assist cone — you snap to the nearest anchor within this angle of your crosshair.
 *
 * Was 12 deg, which made the grapple effectively unusable: a playtester pressed the key
 * repeatedly and "it just didn't do anything". Anchors were in range the whole time, but a
 * 12 deg cone means you must already be looking almost exactly at one, and nothing on screen
 * told the player an anchor was targetable. A traversal verb you cannot find is not a verb.
 *
 * Widened hard. Grappling should feel like the game WANTS you to swing: generous acquisition,
 * with the skill living in the release timing (GRAPPLE_RELEASE_BOOST), not in the aiming.
 */
export const GRAPPLE_AIM_CONE = 32 * (Math.PI / 180);
export const GRAPPLE_PULL_ACCEL = 46.0;
export const GRAPPLE_MAX_SPEED = 30.0;
/** Tether behaves like a spring once taut; these govern the swing. */
export const GRAPPLE_SPRING_K = 18.0;
export const GRAPPLE_DAMPING = 1.6;
/** Releasing near the apex of a swing converts tension into launch speed. The skill is the timing. */
export const GRAPPLE_RELEASE_BOOST = 1.35;
export const GRAPPLE_RELEASE_WINDOW_MS = 220;
export const GRAPPLE_MIN_LENGTH = 3.0;
export const GRAPPLE_COOLDOWN_MS = 500;
export const GRAPPLE_MAX_MS = 3000;
/** Projectile travel speed of the hook itself — not instant, so grapples can be read and dodged. */
export const GRAPPLE_HOOK_SPEED = 90.0;

// ---------------------------------------------------------------------------
// The gun — one per team
// ---------------------------------------------------------------------------

/** THE crux rule: only a clean mid-air catch chambers a round. Floor pickups are empty. */
export const CATCH_CHAMBERS = true;
export const PICKUP_FROM_GROUND_CHAMBERS = false;

/** Catch assist radius — a gun passing this close to a player's hands is catchable. */
export const CATCH_RADIUS = 1.5;
/** Gun must be this far off the ground to count as an "air" catch. */
export const CATCH_MIN_HEIGHT = 0.6;
/** You can't catch your own throw for this long — self-chambering would break the whole design. */
export const THROW_SELF_CATCH_LOCKOUT_MS = 900;
/** Brief input lockout after catching, so catch->fire isn't a zero-frame flick. */
export const CATCH_FIRE_DELAY_MS = 90;

/**
 * THE CATCH IS AN ACTION. You can only catch a gun you are actually LOOKING AT — it must be
 * within this half-angle of your view direction, with clear line of sight.
 *
 * Set near half of FOV_BASE (46 deg) so the rule is self-explaining: if you can see it, you can
 * catch it. This is what makes a pass cost something — the thrower turns to aim it and the
 * receiver turns to catch it, so for that moment neither is aiming at an enemy. That shared
 * window of vulnerability IS the balancing force, at every range.
 *
 * Deliberately NO minimum pass distance or airtime: teammates juggling at point-blank are
 * teammates looking at each other instead of at the enemy. The cone polices itself.
 */
export const CATCH_VIEW_CONE = 50 * (Math.PI / 180);
/** A gun sailing behind cover is not catchable through the cover. */
export const CATCH_REQUIRE_LOS = true;

export const THROW_SPEED = 26.0;
export const THROW_SPEED_CHARGED = 38.0; // hold to charge a longer pass
export const THROW_CHARGE_MS = 600;
export const THROW_UP_BIAS = 0.16; // slight upward arc so passes clear heads
export const THROW_SPIN_RATE = 22.0; // rad/s, visual only
/** Inherit a fraction of the thrower's velocity — passes while sliding actually lead. */
export const THROW_INHERIT_VELOCITY = 0.65;

export const GUN_GRAVITY_SCALE = 0.85;
export const GUN_AIR_DRAG = 0.14;
export const GUN_BOUNCE_RESTITUTION = 0.32;
export const GUN_BOUNCE_FRICTION = 0.55;
export const GUN_REST_SPEED = 0.4; // below this it settles and stops simulating
export const GUN_RADIUS = 0.22;

/** Guns left untouched this long teleport back to a fair spawn, so a round can't deadlock. */
export const GUN_ABANDONED_RESET_MS = 20000;
/** A gun that falls out of the arena respawns after this delay. */
export const GUN_VOID_RESPAWN_MS = 2500;

export const FIRE_COOLDOWN_MS = 250;
/** Hitscan. Range is effectively unlimited within the arena. */
export const BULLET_RANGE = 500.0;

// ---------------------------------------------------------------------------
// Damage + health
// ---------------------------------------------------------------------------

export const MAX_HEALTH = 100;
export const WOUNDED_HEALTH = 40;
/** Headshot = instant kill, always, at any range. Non-negotiable. */
export const HEADSHOT_LETHAL = true;
/** A body shot wounds. A second body shot on a wounded player kills. */
export const BODYSHOT_WOUNDS = true;

export const WOUNDED_SPEED_MULT = 0.86;
export const UNARMED_SPEED_MULT = 1.1;
/** Wounded players leave a visible bleed trail — the enemy can read your team's state. */
export const WOUNDED_BLEED_TRAIL = true;

/** Catching a chambered gun while wounded heals you and spends the chamber. */
export const CATCH_HEALS_WOUNDED = true;
/** Hold this modifier while catching to refuse the heal and keep the round chambered. */
export const CATCH_RAW_MODIFIER = 'ShiftLeft';

export const RESPAWN_DELAY_MS = 0; // rounds are elimination; no mid-round respawn

// ---------------------------------------------------------------------------
// PULSE — the unarmed player's tool
// ---------------------------------------------------------------------------

export const PULSE_RANGE = 3.6;
export const PULSE_CONE = 55 * (Math.PI / 180);
export const PULSE_COOLDOWN_MS = 2600;
export const PULSE_PLAYER_IMPULSE = 15.0;
/** Can knock a gun out of the air mid-pass, or out of an enemy's hands. The highest-value unarmed play. */
export const PULSE_GUN_IMPULSE = 19.0;
export const PULSE_DISARMS = true;
/** Armed players get a weaker pulse, so it's not a strict downgrade to hold the gun. */
export const PULSE_ARMED_MULT = 0.55;

// ---------------------------------------------------------------------------
// Match flow
// ---------------------------------------------------------------------------

export const TEAM_SIZE_MIN = 2;
export const TEAM_SIZE_MAX = 3;
export const ROUNDS_TO_WIN = 7;
export const ROUND_TIME_MS = 90_000;
export const ROUND_WARMUP_MS = 3500;
export const ROUND_END_MS = 4000;
/**
 * HIGH NOON. This long before the round clock expires, every gun in the arena raises a
 * beacon visible through geometry and its hum goes loud and global (DESIGN.md §2).
 *
 * Sudden death escalates **information, never firepower**. There is never a third gun —
 * a team can never actually be disarmed, so the stall a neutral drop would "solve" does
 * not exist. Hiding simply stops working and the round resolves itself.
 */
export const SUDDEN_DEATH_MS = 25_000;
export const MATCH_END_MS = 12_000;

export const TEAM_A = 0;
export const TEAM_B = 1;

// ---------------------------------------------------------------------------
// Networking
// ---------------------------------------------------------------------------

export const PROTOCOL_VERSION = 1;
/** Render this far in the past so snapshot interpolation always has two frames to blend. */
export const INTERP_DELAY_MS = 100;
export const SNAPSHOT_BUFFER_SIZE = 32;
/** Server rewinds hitboxes to the shooter's view time, clamped to this, for lag compensation. */
export const MAX_REWIND_MS = 220;
export const INPUT_BUFFER_SIZE = 64;
/** Reconcile only when the server disagrees by more than this — avoids jitter from float drift. */
export const RECONCILE_POSITION_THRESHOLD = 0.06;
export const RECONCILE_VELOCITY_THRESHOLD = 0.5;
export const HEARTBEAT_MS = 5000;
export const CLIENT_TIMEOUT_MS = 15000;
export const MAX_PLAYERS_PER_ROOM = 6;

/** Room codes: unambiguous alphabet only — no O/0, no I/1, no S/5. Players read these aloud. */
export const ROOM_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRTUVWXYZ2346789';
export const ROOM_CODE_LENGTH = 4;
export const ROOM_CODE_PREFIX = 'ONETAP-';

// ---------------------------------------------------------------------------
// Camera + feel
// ---------------------------------------------------------------------------

export const FOV_BASE = 92;
export const FOV_MAX = 112;
/** Speed at which FOV reaches FOV_MAX. Feeling fast is a rendering job, not a UI job. */
export const FOV_SPEED_REF = 22.0;
export const FOV_DAMP_RATE = 6.0;

export const MOUSE_SENSITIVITY_DEFAULT = 0.0022;
export const PITCH_LIMIT = 89 * (Math.PI / 180);

export const CAMERA_ROLL_DAMP = 9.0;
export const LAND_SHAKE_SCALE = 0.05;
export const FIRE_SHAKE = 0.11;
export const SPEEDLINE_MIN_SPEED = 14.0;

// ---------------------------------------------------------------------------
// Palette — SUNLIT CEL. Bright, clean, modern; Mirror's Edge under a blue sky.
// Renderer, VFX and UI all read from here so the look can never drift between systems.
// See DESIGN.md section 4. Color is spent sparingly and functionally — a mostly-white
// world is what makes a red stairwell a landmark and an amber glow a signal.
// ---------------------------------------------------------------------------

export const PALETTE = {
  /** Sky gradient, horizon -> zenith. */
  sky: 0x8fd4ff,
  skyHigh: 0x4ea8e8,
  sun: 0xfff6e0,

  /** Dominant white architecture, and the pale concrete secondary. */
  base: 0xfafaf8,
  base2: 0xe9ebee,
  /** Shadows are cool blue-grey and LIFTED. Never crush to black. */
  shadow: 0xa9b7cc,
  /** Outline color — soft slate, deliberately not black. Elegant hairline, not marker pen. */
  ink: 0x2a3242,

  teamA: 0x0a84ff,
  teamB: 0xff3b30,

  /** Chambered: warm amber, emissive, blooms. Empty: flat grey, no glow. */
  loaded: 0xffb800,
  empty: 0x9aa0a6,

  /** Wayfinding accents — used as large flat planes that name a region. */
  accentA: 0x12b886,
  accentB: 0xffd43b,

  danger: 0xff3b30,
  neutral: 0xffffff,

  /** Back-compat alias for the old dark `bg`. Deliberately points at the bright sky so any
   *  code still reaching for a "background color" fails toward the new look, not the old one. */
  bg: 0x8fd4ff,
} as const;

export const TEAM_COLORS = [PALETTE.teamA, PALETTE.teamB] as const;
export const TEAM_NAMES = ['AZURE', 'SIGNAL'] as const;

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

export const OUTLINE_THICKNESS = 0.022;
export const TOON_BANDS = 3;
export const BLOOM_STRENGTH = 0.55;
export const BLOOM_THRESHOLD = 0.78;
export const BLOOM_RADIUS = 0.42;
/**
 * Peak chromatic aberration offset, in uv units, at full speed and at the very corner of
 * the frame. Small on purpose: this is speed feedback, not a look. The post chain gates it
 * behind an actual speed threshold and applies it radially, so a stationary player sees
 * exactly zero — visible RGB fringing on static geometry reads as a broken render.
 */
export const CHROMATIC_MAX = 0.0011;
export const SHADOW_MAP_SIZE = 2048;
